import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { getWorkOrderList } from "../../api/workorders";
import { colorFromClientId } from "../CPO/CPOGanttChart";
import { useSnack } from "../../services/SnakbarProvider";
import { handleError } from "../../services/errorHandler";

const LABEL_WIDTH = 240;
const ROW_HEIGHT = 44;
const DAY_WIDTH = 4;

/** Statuses hidden unless "Mostrar terminadas" is toggled on. */
const CLOSED_STATUS_CODES = ["WO_COMPLETED", "WO_CANCELLED"];

const getWindowBounds = () => {
  const year = dayjs().year();
  return {
    rangeStart: dayjs(`${year - 1}-01-01`).startOf("day"),
    rangeEnd: dayjs(`${year + 1}-12-31`).startOf("day"),
  };
};

/**
 * Gantt view of work orders. Each bar spans from the order's creation date to
 * its production date; rows are ordered by CreatedAt ascending.
 */
const WorkOrderGanttChart = ({ companyId }) => {
  const { openSnack } = useSnack();
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showClosed, setShowClosed] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getWorkOrderList({ companyId });
      setRowData(response.data || []);
    } catch (error) {
      openSnack(handleError(error), "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [companyId]);

  const { rangeStart, rangeEnd } = useMemo(() => getWindowBounds(), []);

  const chartRows = useMemo(() => {
    return rowData
      .filter((row) => showClosed || !CLOSED_STATUS_CODES.includes(row.statusCode))
      .filter((row) => row.createdAt && dayjs(row.createdAt).isValid())
      .map((row) => {
        const created = dayjs(row.createdAt).startOf("day");
        const production =
          row.productionDate && dayjs(row.productionDate).isValid()
            ? dayjs(row.productionDate).startOf("day")
            : null;
        const start = production && production.isBefore(created) ? production : created;
        const end = production && production.isAfter(created) ? production : start;
        return {
          ...row,
          created,
          production,
          start,
          end,
        };
      })
      // Keep bars that overlap the 3-year window
      .filter(
        (row) =>
          !row.end.isBefore(rangeStart, "day") &&
          !row.start.isAfter(rangeEnd, "day")
      )
      .sort(
        (a, b) =>
          dayjs(a.createdAt).valueOf() - dayjs(b.createdAt).valueOf() ||
          a.id - b.id
      );
  }, [rowData, showClosed, rangeStart, rangeEnd]);

  const clientLegend = useMemo(() => {
    const byId = new Map();
    chartRows.forEach((row) => {
      const id = row.clientId;
      if (id == null || byId.has(id)) return;
      byId.set(id, {
        id,
        name: row.clientName || `Cliente ${id}`,
        color: colorFromClientId(id),
      });
    });
    return Array.from(byId.values()).sort((a, b) =>
      a.name.localeCompare(b.name, "es")
    );
  }, [chartRows]);

  const { totalDays, monthMarkers, todayOffset } = useMemo(() => {
    const days = Math.max(rangeEnd.diff(rangeStart, "day"), 1);

    const markers = [];
    let cursor = rangeStart.startOf("month");
    while (cursor.isBefore(rangeEnd) || cursor.isSame(rangeEnd, "month")) {
      const offsetDays = cursor.diff(rangeStart, "day");
      if (offsetDays >= 0 && offsetDays <= days) {
        markers.push({
          key: cursor.format("YYYY-MM"),
          label: cursor.format("MMM YYYY"),
          left: offsetDays * DAY_WIDTH,
        });
      }
      cursor = cursor.add(1, "month");
    }

    const today = dayjs().startOf("day");
    const todayDays = today.diff(rangeStart, "day");
    const todayPos =
      todayDays >= 0 && todayDays <= days ? todayDays * DAY_WIDTH : null;

    return {
      totalDays: days,
      monthMarkers: markers,
      todayOffset: todayPos,
    };
  }, [rangeStart, rangeEnd]);

  const timelineWidth = totalDays * DAY_WIDTH;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box width="100%">
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        mb={2}
        flexWrap="wrap"
        useFlexGap
      >
        <Button onClick={() => setShowClosed((prev) => !prev)}>
          {showClosed ? "Ocultar terminadas/canceladas" : "Mostrar terminadas/canceladas"}
        </Button>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {clientLegend.map((client) => (
            <Chip
              key={client.id}
              label={client.name}
              size="small"
              sx={{
                bgcolor: client.color,
                color: "#fff",
                fontWeight: 600,
              }}
            />
          ))}
        </Stack>
      </Stack>

      {chartRows.length === 0 ? (
        <Typography color="text.secondary">
          No hay órdenes de trabajo en la ventana {rangeStart.year()}–
          {rangeEnd.year()}.
        </Typography>
      ) : (
        <Box
          sx={{
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            overflow: "auto",
            maxHeight: 640,
            bgcolor: "background.paper",
          }}
        >
          <Box sx={{ minWidth: LABEL_WIDTH + timelineWidth }}>
            <Box
              sx={{
                display: "flex",
                position: "sticky",
                top: 0,
                zIndex: 2,
                bgcolor: "grey.100",
                borderBottom: 1,
                borderColor: "divider",
                height: 40,
              }}
            >
              <Box
                sx={{
                  width: LABEL_WIDTH,
                  minWidth: LABEL_WIDTH,
                  px: 1.5,
                  display: "flex",
                  alignItems: "center",
                  position: "sticky",
                  left: 0,
                  zIndex: 3,
                  bgcolor: "grey.100",
                  borderRight: 1,
                  borderColor: "divider",
                }}
              >
                <Typography variant="caption" fontWeight={600}>
                  Trabajo / Cliente
                </Typography>
              </Box>
              <Box
                sx={{
                  position: "relative",
                  width: timelineWidth,
                  minWidth: timelineWidth,
                }}
              >
                {monthMarkers.map((marker) => (
                  <Typography
                    key={marker.key}
                    variant="caption"
                    sx={{
                      position: "absolute",
                      left: marker.left,
                      top: 10,
                      pl: 0.5,
                      borderLeft: 1,
                      borderColor: "divider",
                      whiteSpace: "nowrap",
                      color: "text.secondary",
                    }}
                  >
                    {marker.label}
                  </Typography>
                ))}
              </Box>
            </Box>

            {chartRows.map((row) => {
              const clippedStart = row.start.isBefore(rangeStart)
                ? rangeStart
                : row.start;
              const clippedEnd = row.end.isAfter(rangeEnd) ? rangeEnd : row.end;
              const startOffset =
                clippedStart.diff(rangeStart, "day") * DAY_WIDTH;
              const durationDays = Math.max(
                clippedEnd.diff(clippedStart, "day"),
                1
              );
              const fullDurationDays = row.production
                ? Math.max(row.end.diff(row.start, "day"), 1)
                : null;
              const barWidth = durationDays * DAY_WIDTH;
              const barColor = colorFromClientId(row.clientId);
              const title = `${row.originalCode ? `${row.originalCode} · ` : ""}${
                row.name || `OT ${row.id}`
              }`;
              const subtitle = [row.clientName, row.statusName]
                .filter(Boolean)
                .join(" · ");

              return (
                <Box
                  key={row.id}
                  sx={{
                    display: "flex",
                    height: ROW_HEIGHT,
                    borderBottom: 1,
                    borderColor: "divider",
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  <Box
                    sx={{
                      width: LABEL_WIDTH,
                      minWidth: LABEL_WIDTH,
                      px: 1.5,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      position: "sticky",
                      left: 0,
                      zIndex: 1,
                      bgcolor: "background.paper",
                      borderRight: 1,
                      borderColor: "divider",
                    }}
                  >
                    <Typography variant="body2" noWrap title={title} fontWeight={500}>
                      {title}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      noWrap
                      title={subtitle}
                    >
                      {subtitle || "-"}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      position: "relative",
                      width: timelineWidth,
                      minWidth: timelineWidth,
                      bgcolor: "grey.50",
                    }}
                  >
                    {monthMarkers.map((marker) => (
                      <Box
                        key={`${row.id}-${marker.key}`}
                        sx={{
                          position: "absolute",
                          left: marker.left,
                          top: 0,
                          bottom: 0,
                          borderLeft: "1px dashed",
                          borderColor: "divider",
                          pointerEvents: "none",
                        }}
                      />
                    ))}

                    {todayOffset != null && (
                      <Box
                        sx={{
                          position: "absolute",
                          left: todayOffset,
                          top: 0,
                          bottom: 0,
                          width: 2,
                          bgcolor: "error.light",
                          opacity: 0.7,
                          pointerEvents: "none",
                          zIndex: 1,
                        }}
                      />
                    )}

                    <Tooltip
                      title={
                        <Box>
                          <Typography variant="body2">{title}</Typography>
                          <Typography variant="caption" display="block">
                            Cliente: {row.clientName || "-"}
                          </Typography>
                          <Typography variant="caption" display="block">
                            Creada: {row.created.format("DD/MM/YYYY")}
                          </Typography>
                          <Typography variant="caption" display="block">
                            Producción:{" "}
                            {row.production
                              ? row.production.format("DD/MM/YYYY")
                              : "Sin fecha"}
                          </Typography>
                          <Typography variant="caption" display="block">
                            Cantidad: {row.quantity ?? "-"}
                          </Typography>
                          <Typography variant="caption" display="block">
                            Estatus: {row.statusName || "-"}
                          </Typography>
                          {fullDurationDays != null && (
                            <Typography variant="caption" display="block">
                              Duración: {fullDurationDays} día
                              {fullDurationDays === 1 ? "" : "s"}
                            </Typography>
                          )}
                        </Box>
                      }
                    >
                      <Box
                        sx={{
                          position: "absolute",
                          left: startOffset,
                          top: 10,
                          width: Math.max(barWidth, 6),
                          height: 24,
                          borderRadius: 1,
                          bgcolor: barColor,
                          boxShadow: 1,
                          cursor: "default",
                          zIndex: 2,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          overflow: "hidden",
                          // Orders without a production date render as a hatched marker
                          ...(row.production
                            ? {}
                            : {
                                opacity: 0.6,
                                backgroundImage:
                                  "repeating-linear-gradient(45deg, rgba(255,255,255,0.35) 0 3px, transparent 3px 6px)",
                              }),
                        }}
                      >
                        {barWidth > 80 && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#fff",
                              fontWeight: 600,
                              whiteSpace: "nowrap",
                              textShadow: "0 0 2px rgba(0,0,0,0.4)",
                            }}
                          >
                            {row.name || row.clientName || ""}
                          </Typography>
                        )}
                      </Box>
                    </Tooltip>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      <Typography variant="caption" color="text.secondary" mt={1} display="block">
        Ordenadas por fecha de creación. Barra: creación → fecha de producción
        (rayada si no tiene fecha de producción). Color por cliente. Ventana:{" "}
        {rangeStart.format("DD/MM/YYYY")} – {rangeEnd.format("DD/MM/YYYY")}. Línea
        roja: hoy ({dayjs().format("DD/MM/YYYY")}).
      </Typography>
    </Box>
  );
};

export default WorkOrderGanttChart;
